// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { ProductListPageContext } from "@gcg/Overrides/Pages/ProductListPage";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { FC, useContext, useState } from "react";
import { css } from "styled-components";

/* eslint-disable */
const HTMLEllipsis = require("@gcg/Libraries/react-lines-ellipsis/html.js");
/* eslint-enable */

interface Props extends WidgetProps {}

export interface BrandDetailsContentStyles {
    container?: InjectableCss;
}

export const contentStyles: BrandDetailsContentStyles = {
    container: {
        css: css`
            padding-right: 30px;

            * {
                line-height: 22px;
            }

            p {
                &:last-child {
                    margin-bottom: 0;
                }
            }

            .LinesEllipsis-ellipsis,
            a {
                color: ${getColor("primary.main")};
                cursor: pointer;

                &:hover,
                &:focus {
                    &,
                    span {
                        text-decoration: underline;
                    }
                }
            }
        `,
    },
};

const styles = contentStyles;

const BrandDetailsContent: FC<Props> = () => {
    const [useEllipsis, setUseEllipsis] = useState(true);
    const { value: brand } = useContext(BrandStateContext);

    if (!brand || !brand.htmlContent) {
        return null;
    }
    // if there is a body tag, keep only what's between the body tag, then change those characters so the whole string is wrapped in a div tag instead
    let updatedHtmlContent = brand.htmlContent;
    const bodyTag = brand.htmlContent.indexOf("<body");
    if (bodyTag > -1) {
        updatedHtmlContent = `<div${brand.htmlContent.substring(
            bodyTag + 5,
            brand.htmlContent.indexOf("</body>"),
        )}</div>`;
    }

    const handleTextClick = (e: any) => {
        const target = e.target;

        if (target.id === "BrandDetailsContent_readMore") {
            e.preventDefault();
            setUseEllipsis(false);
        }
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="brandContent" onClick={handleTextClick}>
            {useEllipsis && (
                <HTMLEllipsis
                    unsafeHTML={brand.htmlContent}
                    maxLine="3"
                    ellipsisHTML="... <span id='BrandDetailsContent_readMore'>read more</span>"
                    basedOn="letters"
                />
            )}
            {!useEllipsis && parse(updatedHtmlContent, parserOptions)}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BrandDetailsContent,
    definition: {
        group: "Brand Details",
        displayName: "Content",
        allowedContexts: ["BrandDetailsPage", "ProductListPage"],
    },
};

export default widgetModule;
